import { useEffect, useMemo, useState } from 'react';

import { getNearFacilities } from '../services/facility.service';
import { getGeofences } from '../services/geofence.service';
import { Facility } from '../types/Facility';
import { Geofence } from '../types/Geofence';
import { JourneyFacility } from '../components/pages/device/VisitJourneyMap/VisitJourneyPanel';

export interface VisitGeofence {
  facilityId: string;
  facilityName: string;
  /** Leaflet-ready polygon ring: [lat, lng][] (already swapped from API's [lng, lat]) */
  positions: [number, number][];
  /** Geofence centroid — used for trail points */
  centroid: { latitude: number; longitude: number } | null;
  /** Colour tier: 'green' = most recent closest facility, 'yellow' = in journey, 'purple' = other */
  tier: 'green' | 'yellow' | 'purple';
}

// Colour tokens — referenced from the reader Map conventions
export const GEOFENCE_COLORS = {
  green: '#16a34a', // most recent closest facility
  yellow: '#eab308', // facility that is part of the closest-facility journey
  purple: 'purple', // standard (non-journey) facility
} as const;

interface UseVisitGeofencesParams {
  location?: { latitude: number; longitude: number } | null;
  journeyFacilities: JourneyFacility[];
  distanceInMeters?: number;
  shouldFetch?: boolean;
}

// Build a getGeofences request filtered by facilityId
const facilityGeofenceRequest = (facilityId: string) => ({
  'Filters[0].fieldName': 'facilityId',
  'Filters[0].fieldValue': facilityId,
  Take: 50,
});

/**
 * Fetches all facility geofences in the area around the device location and
 * tags each with a colour tier based on whether its facility appears in the
 * closest-facility journey, and whether it is the most-recently-seen closest
 * facility.
 *
 * Flow:
 *   1. getNearFacilities(location) → all facilities in the area (their IDs/names)
 *   2. getGeofences(facilityId) for each facility → real polygon geofences
 *   3. tag each geofence green / yellow / purple
 */
export function useVisitGeofences({ location, journeyFacilities, distanceInMeters = 10000, shouldFetch = true }: UseVisitGeofencesParams) {
  const [rawGeofences, setRawGeofences] = useState<Geofence[]>([]);
  const [facilityNameById, setFacilityNameById] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!shouldFetch || !location?.latitude || !location?.longitude) {
      setRawGeofences([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        // 1. All facilities in the area
        const nearRes: any = await getNearFacilities({
          lat: location.latitude,
          lng: location.longitude,
          distanceInMeters,
          skip: 0,
          take: 1000,
        } as any);

        const facilitiesInArea: Facility[] = nearRes?.data?.results ?? nearRes?.results ?? [];
        if (cancelled) return;

        const nameMap = new Map<string, string>();
        facilitiesInArea.forEach((f: any) => {
          const fid = f.id ?? f.facilityId;
          if (fid) nameMap.set(fid, f.name ?? f.facilityName ?? '');
        });
        setFacilityNameById(nameMap);

        // 2. If near-facilities already embed geofences, use them directly.
        const embedded: Geofence[] = [];
        facilitiesInArea.forEach((f: any) => {
          if (Array.isArray(f.geofences) && f.geofences.length > 0) {
            f.geofences.forEach((g: Geofence) => {
              embedded.push({ ...g, facilityId: g.facilityId ?? f.id ?? f.facilityId, facilityName: g.facilityName ?? f.name });
            });
          }
        });

        if (embedded.length > 0) {
          if (!cancelled) setRawGeofences(embedded);
          return;
        }

        // 3. Otherwise fetch geofences per facility via getGeofences(facilityId)
        const ids = facilitiesInArea.map((f: any) => f.id ?? f.facilityId).filter(Boolean) as string[];

        const results = await Promise.all(
          ids.map((fid) =>
            getGeofences(facilityGeofenceRequest(fid) as any)
              .then((res: any) => {
                const gs: Geofence[] = res?.data?.results ?? res?.results ?? [];
                // ensure facilityId tag is present for matching
                return gs.map((g) => ({ ...g, facilityId: g.facilityId ?? fid, facilityName: g.facilityName ?? nameMap.get(fid) }));
              })
              .catch(() => [] as Geofence[]),
          ),
        );

        if (cancelled) return;
        setRawGeofences(results.flat());
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to fetch area geofences:', e);
          setRawGeofences([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location?.latitude, location?.longitude, distanceInMeters, shouldFetch]);

  const journeyFacilityIds = useMemo(() => new Set(journeyFacilities.map((f) => f.facilityId).filter(Boolean)), [journeyFacilities]);

  const mostRecentFacilityId = useMemo(() => {
    if (!journeyFacilities.length) return null;
    let latest: JourneyFacility | null = null;
    for (const f of journeyFacilities) {
      if (!f.lastSeenDateTime) continue;
      if (!latest || new Date(f.lastSeenDateTime).getTime() > new Date(latest.lastSeenDateTime).getTime()) {
        latest = f;
      }
    }
    return latest?.facilityId ?? null;
  }, [journeyFacilities]);

  const geofences = useMemo<VisitGeofence[]>(() => {
    const out: VisitGeofence[] = [];

    rawGeofences.forEach((geo: any) => {
      if (!geo?.coordinates || geo.coordinates.length === 0) return;

      const facId = geo.facilityId ?? '';
      const facName = geo.facilityName ?? facilityNameById.get(facId) ?? geo.name ?? '';

      // API coordinates are [lng, lat]; Leaflet wants [lat, lng]
      const positions = (geo.coordinates as [number, number][]).map(([lng, lat]) => [lat, lng] as [number, number]);

      const tier: VisitGeofence['tier'] =
        facId && facId === mostRecentFacilityId ? 'green' : facId && journeyFacilityIds.has(facId) ? 'yellow' : 'purple';

      out.push({
        facilityId: facId,
        facilityName: facName,
        positions,
        centroid: geo.location ? { latitude: geo.location.latitude, longitude: geo.location.longitude } : null,
        tier,
      });
    });

    return out;
  }, [rawGeofences, facilityNameById, journeyFacilityIds, mostRecentFacilityId]);

  const centroidByFacilityId = useMemo(() => {
    const map = new Map<string, { latitude: number; longitude: number }>();
    geofences.forEach((g) => {
      if (g.facilityId && g.centroid && !map.has(g.facilityId)) {
        map.set(g.facilityId, g.centroid);
      }
    });
    return map;
  }, [geofences]);

  return { geofences, centroidByFacilityId, loading };
}
